"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Menu, X, Phone, Mail } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"

const navLinks = [
  { href: "#home", label: "Strona główna" },
  { href: "#uslugi", label: "Usługi" },
  { href: "#o-nas", label: "O nas" },
  { href: "#opinie", label: "Opinie" },
  { href: "#kontakt", label: "Kontakt" },
]

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-primary/95 backdrop-blur-md shadow-lg"
          : "bg-primary"
      }`}
    >
      {/* Top bar with contact info */}
      <div className="hidden md:block bg-primary border-b border-primary-foreground/10">
        <div className="container mx-auto px-4 py-2 flex justify-end items-center gap-6 text-sm text-primary-foreground/80">
          <a href="tel:+48508391393" className="flex items-center gap-2 hover:text-accent transition-colors">
            <Phone className="h-3.5 w-3.5" />
            <span>508 391 393</span>
          </a>
          <a href="mailto:kontakt@biurorzymska.pl" className="flex items-center gap-2 hover:text-accent transition-colors">
            <Mail className="h-3.5 w-3.5" />
            <span>kontakt@biurorzymska.pl</span>
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link href="#home" className="flex items-center gap-3">
            <div className="relative">
              <span className="font-serif text-3xl md:text-4xl font-bold text-accent">R</span>
              <span className="absolute -top-1 -right-1 text-accent text-xs">✦</span>
            </div>
            <div className="hidden sm:block">
              <p className="text-primary-foreground font-semibold text-sm tracking-wide uppercase">Biuro Rachunkowe</p>
              <p className="text-accent font-serif text-lg">Elżbieta Rzymska</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-primary-foreground/90 hover:text-accent transition-colors font-medium text-sm uppercase tracking-wide"
              >
                {link.label}
              </Link>
            ))}
            <Button
              asChild
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
            >
              <Link href="#kontakt">Umów wizytę</Link>
            </Button>
          </div>

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-primary-foreground">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Otwórz menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-primary border-primary w-[300px]">
              <div className="flex flex-col gap-6 mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-serif text-3xl font-bold text-accent">R</span>
                  <div>
                    <p className="text-primary-foreground font-semibold text-sm">Biuro Rachunkowe</p>
                    <p className="text-accent font-serif">Elżbieta Rzymska</p>
                  </div>
                </div>
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsOpen(false)}
                    className="text-primary-foreground/90 hover:text-accent transition-colors font-medium text-lg py-2 border-b border-primary-foreground/10"
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="flex flex-col gap-3 mt-4 text-primary-foreground/80 text-sm">
                  <a href="tel:+48508391393" className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-accent" />
                    508 391 393
                  </a>
                  <a href="mailto:kontakt@biurorzymska.pl" className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-accent" />
                    kontakt@biurorzymska.pl
                  </a>
                </div>
                <Button
                  asChild
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold mt-4"
                >
                  <Link href="#kontakt" onClick={() => setIsOpen(false)}>
                    Umów wizytę
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  )
}
